// Where the league site's sign-in returns to (registration plan decision 21).
// Pure helpers, no React. The site's counterpart of the admin's
// admin-v2/app/auth/returnTo.ts › safeAdminNext, which site code may not
// import (test/site/lint/isolation.test.ts rule 3).
//
// `next` is advisory: anyone can put anything in a URL, so it is re-validated
// before every navigation. Only a path on this origin passes: never a scheme,
// a protocol-relative '//host', a backslash (browsers read '/\host' as
// '//host'), a control character (the URL parser strips tab and newline, so
// '/\t/evil' would become '//evil'), the sign-in page itself (a loop), the
// staff admin, the API, an OBS overlay or the password recovery pages,
// percent-encoded or not and in any case (React Router matches decoded paths
// case-insensitively).
//
// The recovery pages (plan step 9) are refused on purpose: /reset-password/
// <token> carries a credential in its path (security-model.md quirk 60), and
// the page strips it with history.replaceState, which bypasses React Router,
// so useLocation() on that page still holds /reset-password/<token>. No URL
// may ever be built from it: a header "Sign in" link computed there gets a
// plain /sign-in (signInPathFor), never ?next=/reset-password/<token>.
// /forgot-password is refused too: returning there after signing in is
// pointless.
//
// /register/join (plan step 16) is refused the same way: its URL carries the
// invitation token in ?invite_token=, and the page strips it with
// history.replaceState, so the router location still holds ?invite_token=
// after the strip. signInPathFor gives a plain /sign-in there.
//
// /register/join may also carry `?team_code=` (plan step 26, teamCode.ts).
// A team code is not stripped: the page keeps it in the address bar,
// canonicalized (/register/join?team_code=<CODE>), so a refresh or a shared
// link still works. BLOCKED already refuses /register/join, so no URL is
// ever built from that location either.

const MAX_NEXT = 2048
const BLOCKED = /^\/(sign-in|admin|api|forgot-password|reset-password|register\/join)(\/|$)/i
const OBS = /^\/obs\//i

export const SIGN_IN_PATH = '/sign-in'
/** My account (plan step 9). */
export const ACCOUNT_PATH = '/account'
/** "Forgot password?" (plan step 9). */
export const FORGOT_PASSWORD_PATH = '/forgot-password'
/** Where a reset link lands, and what the address bar shows once the token is stripped. */
export const RESET_PASSWORD_PATH = '/reset-password'
/** The invitation page (plan step 16), and what the address bar shows once the token is stripped. */
export const JOIN_PATH = '/register/join'
/** The invitation link's query parameter (member-links.ts › memberInviteUrl). */
export const INVITE_TOKEN_PARAM = 'invite_token'

/** True for /sign-in (and /sign-in/), any case. */
export function isSignInPath(pathname: string): boolean {
  return /^\/sign-in\/?$/i.test(pathname)
}

export function safeSiteNext(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw === '' || raw.length > MAX_NEXT) return null
  if (!raw.startsWith('/') || raw.startsWith('//')) return null
  if (raw.includes('\\')) return null
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(raw)) return null
  let u: URL
  try {
    u = new URL(raw, window.location.origin)
  } catch {
    return null
  }
  if (u.origin !== window.location.origin) return null
  if (u.pathname.startsWith('//')) return null
  let decoded: string
  try {
    decoded = decodeURIComponent(u.pathname)
  } catch {
    return null
  }
  // '/%2F%2Fevil' decodes to '///evil'; '/%5Cevil' to '/\evil'.
  if (decoded.startsWith('//') || decoded.includes('\\')) return null
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(decoded)) return null
  const path = decoded.replace(/\/{2,}/g, '/')
  if (BLOCKED.test(path) || OBS.test(path)) return null
  return u.pathname + u.search + u.hash
}

/** The sign-in URL for the page someone is on: with ?next= when it is safe to return to. */
export function signInPathFor(loc: { pathname: string; search: string; hash: string }): string {
  if (loc.pathname === '/') return SIGN_IN_PATH
  // Already on the sign-in page: keep its own ?next=.
  if (isSignInPath(loc.pathname)) return loc.pathname + loc.search + loc.hash
  const safe = safeSiteNext(loc.pathname + loc.search + loc.hash)
  return safe ? `${SIGN_IN_PATH}?next=${encodeURIComponent(safe)}` : SIGN_IN_PATH
}
